'use client';

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import EventFormModal, { type CalendarEvent, type UserOption, type TeamOption } from './EventFormModal';

type NewEventDefaults = {
  defaultDate?: Date;
  title?: string; description?: string;
  refType?: string; refId?: string;
  accountId?: number; accountName?: string; accountPohodaId?: string;
  contactId?: number; contactName?: string;
  leadId?: number; leadName?: string;
  phoneNumber?: string;
  impulseType?: string; impulseKey?: string;
};

type EventModalContextValue = {
  openEvent: (eventId: number) => void;
  openNewEvent: (defaults?: NewEventDefaults) => void;
};

const EventModalContext = createContext<EventModalContextValue | null>(null);

export function useEventModal(): EventModalContextValue {
  const ctx = useContext(EventModalContext);
  if (!ctx) {
    // Fallback: if no provider, return no-op (page still works, just no modal)
    return { openEvent: () => {}, openNewEvent: () => {} };
  }
  return ctx;
}

export function EventModalProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, mounted } = useAuth();
  const { mutate } = useSWRConfig();
  const [open, setOpen] = useState(false);
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [defaultDate, setDefaultDate] = useState<Date | undefined>(undefined);
  const [pendingEventId, setPendingEventId] = useState<number | null>(null);
  const [newEventDefaults, setNewEventDefaults] = useState<NewEventDefaults | null>(null);

  // Fetch user/team options once (shared by all modal openings)
  const { data: userOptions } = useSWR<UserOption[]>(
    mounted && isAuthenticated ? '/api/user/calendar/users' : null,
  );
  const { data: teamOptions } = useSWR<TeamOption[]>(
    mounted && isAuthenticated ? '/api/user/calendar/teams' : null,
  );

  // Fetch event by ID when requested
  const { data: fetchedEvent, mutate: mutateFetched } = useSWR<CalendarEvent>(
    pendingEventId != null ? `/api/user/calendar/events/${pendingEventId}` : null,
    (url: string) => apiFetch<CalendarEvent>(url, { silent: true }),
    { revalidateOnFocus: false },
  );

  // When event is fetched, open the modal
  const handleEventReady = useCallback(() => {
    if (fetchedEvent && pendingEventId != null) {
      setEvent(fetchedEvent);
      setOpen(true);
      setPendingEventId(null);
    }
  }, [fetchedEvent, pendingEventId]);

  // Trigger fetch when openEvent is called
  const openEvent = useCallback((eventId: number) => {
    setPendingEventId(eventId);
    // Force re-fetch in case it was cached
    mutateFetched();
  }, [mutateFetched]);

  const openNewEvent = useCallback((defaults?: NewEventDefaults) => {
    setEvent(null);
    setDefaultDate(defaults?.defaultDate);
    setNewEventDefaults(defaults ?? null);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setEvent(null);
    setDefaultDate(undefined);
    setNewEventDefaults(null);
  }, []);

  const handleSaved = useCallback((savedEvent: CalendarEvent, isNew: boolean) => {
    // Update the modal's event in place so the user sees the saved state
    setEvent(savedEvent);
    // Revalidate all calendar event lists (by-ref, by-account, calendar grid…)
    mutate((key) => typeof key === 'string' && key.startsWith('/api/user/calendar/events'));
    if (isNew) {
      // Optionally could navigate or notify; for now just keep modal open
    }
  }, [mutate]);

  const handleDeleted = useCallback((id: number) => {
    setOpen(false);
    setEvent(null);
  }, []);

  // Apply fetched event when it arrives
  if (fetchedEvent && pendingEventId != null && !open) {
    handleEventReady();
  }

  const value: EventModalContextValue = {
    openEvent,
    openNewEvent,
  };

  return (
    <EventModalContext.Provider value={value}>
      {children}
      <EventFormModal
        open={open}
        event={event}
        defaultType="TASK"
        allowTypeChange={true}
        defaultDate={defaultDate}
        newEventDefaults={newEventDefaults}
        userOptions={userOptions || []}
        teamOptions={teamOptions || []}
        onClose={handleClose}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </EventModalContext.Provider>
  );
}
